import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import {
  LightBulbIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ChartBarIcon,
  TrendingUpIcon,
  ChatIcon,
  RefreshIcon,
  DocumentTextIcon,
  ExclamationIcon,
  CheckCircleIcon,
} from '@heroicons/react/outline';

const AIBasicsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  
  const tabs = [
    { name: 'What is AI?', icon: <LightBulbIcon className="w-5 h-5" /> },
    { name: 'Benefits', icon: <TrendingUpIcon className="w-5 h-5" /> },
    { name: 'AI Terms', icon: <ChatIcon className="w-5 h-5" /> },
    { name: 'Myths vs. Reality', icon: <ExclamationIcon className="w-5 h-5" /> },
    { name: 'Getting Started', icon: <CheckCircleIcon className="w-5 h-5" /> },
  ];
  
  const benefits = [
    {
      icon: <CurrencyDollarIcon className="w-6 h-6" />,
      title: 'Cost Reduction',
      description: 'Automate repetitive tasks to reduce operational costs and allocate resources more effectively.',
    },
    {
      icon: <ClockIcon className="w-6 h-6" />,
      title: 'Time Savings',
      description: 'Let AI handle time-consuming tasks while you focus on strategic business growth.',
    },
    {
      icon: <ChartBarIcon className="w-6 h-6" />,
      title: 'Better Decision-Making',
      description: 'Gain data-driven insights to make more informed business decisions.',
    },
    {
      icon: <TrendingUpIcon className="w-6 h-6" />,
      title: 'Competitive Advantage',
      description: 'Leverage technology to stand out in your market and outperform competitors.',
    },
  ];
  
  const aiTerms = [
    {
      term: 'Machine Learning',
      definition: 'Software that improves as it processes more of your business data. Think of it as an employee who gets better at their job over time without needing explicit training.',
    },
    {
      term: 'Natural Language Processing',
      definition: 'Technology that understands and responds to normal human language. This allows computers to interpret text or speech the way people naturally communicate.',
    },
    {
      term: 'Automation',
      definition: 'Systems that handle repetitive tasks without human intervention. Like having a virtual assistant that works 24/7 handling routine operations.',
    },
    {
      term: 'Data Analysis',
      definition: 'Finding valuable patterns and insights in your business information. Imagine having an expert who can instantly spot trends in thousands of customer transactions.',
    },
    {
      term: 'Algorithm',
      definition: 'A set of rules or instructions that the AI follows to solve problems. Think of it as a recipe that tells the computer exactly what steps to take.',
    },
  ];
  
  const myths = [
    {
      myth: 'AI is only for big corporations with big budgets',
      reality: 'Open-source AI tools make this technology accessible to businesses of all sizes. SMMEs can now implement targeted AI solutions at affordable price points.',
    },
    {
      myth: 'You need technical expertise to use AI',
      reality: 'Our solutions are designed to be user-friendly, requiring minimal technical knowledge. We handle the complex technical aspects while you focus on using the insights.',
    },
    {
      myth: 'AI will replace human jobs',
      reality: 'AI works best as a complement to human skills, handling repetitive tasks while freeing people to focus on creativity, strategy, and customer relationships.',
    },
    {
      myth: 'AI implementation is complicated and time-consuming',
      reality: 'With our pre-configured solutions, implementation typically takes just 1-2 weeks with minimal disruption to your business operations.',
    },
  ];
  
  const steps = [
    {
      number: '01',
      title: 'Identify Your Business Challenge',
      description: 'What specific operational or strategic challenge is your business facing? This helps us recommend the most suitable AI solution.',
    },
    {
      number: '02',
      title: 'Select the Appropriate AI Solution',
      description: 'Browse our catalogue and choose the tool that addresses your specific needs. Not sure which one? Our team can help guide you.',
    },
    {
      number: '03',
      title: 'We Handle the Technical Setup',
      description: 'Provide us with the necessary information, and our team will configure and deploy the AI solution for your business.',
    },
    {
      number: '04',
      title: 'Start Using AI-Powered Insights',
      description: 'Begin leveraging your new AI tool to improve operations, make better decisions, and drive business growth.',
    },
  ];
  
  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 } 
    }
  };
  
  return (
    <Layout>
      {/* Hero section */}
      <section className="pt-32 pb-16 bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="px-4 mx-auto max-w-5xl sm:px-6 lg:px-8 text-center">
          <div className="space-y-4">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-accent font-heading">Resources & Literacy</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground dark:text-dark-foreground">
              Systems & <span className="font-serif italic text-accent font-light">Automation Basics</span>
            </h1>
            <p className="max-w-2xl mx-auto text-sm text-foreground/60 dark:text-dark-foreground/60 font-light leading-relaxed">
              No technical jargon—just clear, human explanations of how digital automation and custom software solve real business challenges.
            </p>
          </div>
        </div>
      </section>
      
      {/* Main content with tabs - Use background for main section */}
      <section className="py-12 bg-background dark:bg-dark-background">
        <div className="px-4 mx-auto max-w-5xl sm:px-6 lg:px-8">
          {/* Tabs - Use border color, accent for active state */}
          <div className="border-b border-border dark:border-dark-border mb-8">
            <nav className="flex -mb-px space-x-6 overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab, index) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveTab(index)}
                  className={`
                    whitespace-nowrap pb-4 px-1 border-b-2 font-semibold text-xs tracking-wider uppercase flex items-center transition-colors outline-none
                    ${
                      activeTab === index
                        ? 'border-accent text-accent'
                        : 'border-transparent text-foreground/50 hover:text-foreground hover:border-border dark:text-dark-foreground/50 dark:hover:text-dark-foreground dark:hover:border-dark-border'
                    }
                  `}
                >
                  <span className="mr-1.5">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Tab content */}
          <div className="mt-8">
            {/* What is AI? */}
            {activeTab === 0 && (
              <motion.div
                key="what-is-ai"
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
                // Apply semantic prose styles
                className="prose prose-lg dark:prose-invert max-w-none" 
              >
                {/* Use semantic heading color */}
                <h2 className="mb-6 text-3xl font-bold text-primary dark:text-dark-accent">What is AI (In Plain Language)?</h2>
                
                {/* Use secondary background for highlighted box */}
                <div className="p-8 mb-8 bg-secondary dark:bg-dark-secondary rounded-xl">
                  <p className="text-xl text-secondary-foreground dark:text-dark-secondary-foreground">
                    {/* Ensure strong tag inherits correct color */}
                    <strong className="dark:text-dark-secondary-foreground">Artificial Intelligence (AI)</strong> refers to computer systems designed to perform tasks that typically require human intelligence. Think of AI as a smart assistant that can learn, analyze information, and make decisions to help your business.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 gap-8 mb-8 md:grid-cols-2">
                  {/* Use card styles */}
                  <div className="p-6 rounded-lg bg-card dark:bg-dark-card shadow-soft dark:shadow-dark-card">
                    <h3 className="mb-4 text-xl font-semibold text-primary dark:text-dark-accent">Traditional Software vs. AI</h3>
                    <p className="text-card-foreground dark:text-dark-card-foreground">Traditional software follows specific instructions and doesn't adapt. It's like a calculator that performs exactly what you ask.</p>
                    <p className="mt-4 text-card-foreground dark:text-dark-card-foreground">AI, on the other hand, can learn from data, adapt to new inputs, and improve over time. It's like having an assistant who gets better at their job with experience.</p>
                  </div>
                  
                  {/* Use card styles */}
                  <div className="p-6 rounded-lg bg-card dark:bg-dark-card shadow-soft dark:shadow-dark-card">
                    <h3 className="mb-4 text-xl font-semibold text-primary dark:text-dark-accent">How AI Works for Business</h3>
                    <p className="text-card-foreground dark:text-dark-card-foreground">AI tools analyze your business data to identify patterns, make predictions, and automate tasks. This helps you:</p>
                    {/* Use muted foreground for list items? */}
                    <ul className="mt-4 list-disc list-inside text-muted-foreground dark:text-dark-muted-foreground">
                      <li>Save time on repetitive tasks</li>
                      <li>Discover insights you might have missed</li>
                      <li>Make data-driven decisions</li>
                      <li>Personalize customer experiences</li>
                    </ul>
                  </div>
                </div>
                
                {/* Use primary colors for accent border box */}
                <div className="p-6 border-l-4 rounded-lg bg-primary/5 dark:bg-dark-primary/10 border-primary dark:border-dark-accent">
                  <h3 className="mb-2 text-xl font-semibold text-primary dark:text-dark-accent">The AI Advantage for SMMEs</h3>
                  <p className="text-foreground dark:text-dark-secondary-foreground">
                    What makes AI particularly valuable for small and medium enterprises is that it provides capabilities that previously only large corporations could afford. Using open-source tools and focused solutions, SMMEs can now leverage AI to solve specific business challenges without massive investments in technology or specialized staff.
                  </p>
                </div>
              </motion.div>
            )}
            
            {/* Benefits */}
            {activeTab === 1 && (
              <motion.div
                key="benefits"
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <h2 className="mb-6 text-3xl font-bold text-primary dark:text-dark-accent">AI Benefits for Your Business</h2>
                
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      // Use card styles
                      className="p-6 rounded-lg bg-card dark:bg-dark-card shadow-soft dark:shadow-dark-card"
                    >
                      {/* Use primary/accent for icon box */}
                      <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-md bg-primary/10 dark:bg-dark-accent/10 text-primary dark:text-dark-accent">
                        {benefit.icon}
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-card-foreground dark:text-dark-card-foreground">{benefit.title}</h3>
                      <p className="text-muted-foreground dark:text-dark-muted-foreground">{benefit.description}</p>
                    </motion.div>
                  ))}
                </div>
                
                {/* Use secondary background for this info box */}
                <div className="p-8 mt-12 rounded-lg bg-secondary dark:bg-dark-secondary">
                  <h3 className="mb-4 text-2xl font-semibold text-primary dark:text-dark-accent">Real-World Benefits for South African SMMEs</h3>
                  
                  <div className="space-y-6">
                    {/* Example Benefit Item - Use success color for icon bg? */}
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-success/10 dark:bg-dark-success/10 text-success dark:text-dark-success">
                          <RefreshIcon className="w-5 h-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-secondary-foreground dark:text-dark-secondary-foreground">Overcoming Resource Limitations</h4>
                        <p className="mt-1 text-muted-foreground dark:text-dark-muted-foreground">AI tools allow small teams to accomplish tasks that would typically require more staff, helping SMMEs overcome resource constraints.</p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-success/10 dark:bg-dark-success/10 text-success dark:text-dark-success">
                          <LightBulbIcon className="w-5 h-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-secondary-foreground dark:text-dark-secondary-foreground">Bridging Skills Gaps</h4>
                        <p className="mt-1 text-muted-foreground dark:text-dark-muted-foreground">AI can compensate for skills shortages by providing expertise in areas like data analysis, market research, or content creation.</p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-success/10 dark:bg-dark-success/10 text-success dark:text-dark-success">
                          <ChartBarIcon className="w-5 h-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-secondary-foreground dark:text-dark-secondary-foreground">Competing with Larger Companies</h4>
                        <p className="mt-1 text-muted-foreground dark:text-dark-muted-foreground">AI tools level the playing field, allowing smaller businesses to deliver personalized services and make data-driven decisions like their larger competitors.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {/* AI Terms */}
            {activeTab === 2 && (
              <motion.div
                key="ai-terms"
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <h2 className="mb-6 text-3xl font-bold text-primary dark:text-dark-accent">Understanding Key AI Terms</h2>
                
                <div className="space-y-6">
                  {aiTerms.map((item, index) => (
                    // Use secondary/card colors for disclosure
                    <Disclosure key={index} as="div" className="rounded-lg shadow-sm bg-secondary dark:bg-dark-secondary">
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-lg font-medium text-left rounded-t-lg text-primary dark:text-dark-accent hover:bg-secondary/80 dark:hover:bg-dark-secondary/80 focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                            <span>{item.term}</span>
                            <ChevronDownIcon
                              className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                            />
                          </Disclosure.Button>
                          {/* Use card colors for panel content */}
                          <Disclosure.Panel className="px-6 py-4 text-base rounded-b-lg text-muted-foreground dark:text-dark-muted-foreground bg-card dark:bg-dark-card">
                            {item.definition}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </div>
                
                {/* Use primary color box */}
                <div className="p-6 mt-12 rounded-lg bg-primary dark:bg-dark-primary text-primary-foreground dark:text-dark-primary-foreground">
                  <h3 className="mb-4 text-xl font-semibold">Why Understanding These Terms Matters</h3>
                  <p className="opacity-80">
                    Knowing these basic concepts helps you have more productive conversations about AI solutions for your business. You don't need to become a technical expert—just understanding the fundamentals will help you make informed decisions about which AI tools can best address your specific business challenges.
                  </p>
                </div>
              </motion.div>
            )}
            
            {/* Myths vs. Reality */}
            {activeTab === 3 && (
              <motion.div
                key="myths"
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <h2 className="mb-6 text-3xl font-bold text-primary dark:text-dark-accent">AI Myths vs. Reality</h2>
                <div className="space-y-8">
                  {myths.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.15, duration: 0.5 }}
                      // Use card colors
                      className="overflow-hidden rounded-lg bg-card dark:bg-dark-card shadow-soft dark:shadow-dark-card"
                    >
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-card-foreground dark:text-dark-card-foreground">
                          {/* Use destructive for myth text */}
                          Myth: <span className="text-destructive dark:text-dark-destructive">{item.myth}</span> 
                        </h3>
                      </div>
                      {/* Use secondary/success for reality */}
                      <div className="px-6 py-4 border-t bg-secondary dark:bg-dark-secondary border-border dark:border-dark-border">
                        <p className="text-base text-secondary-foreground dark:text-dark-secondary-foreground">
                          <strong className="text-success dark:text-dark-success">Reality:</strong> {item.reality}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Getting Started */}
            {activeTab === 4 && (
              <motion.div
                key="getting-started"
                variants={fadeInVariants}
                initial="hidden"
                animate="visible"
              >
                <h2 className="mb-8 text-3xl font-bold text-primary dark:text-dark-accent">Getting Started with AI</h2>
                <div className="relative">
                  {/* Use border color for line */}
                  <div className="absolute w-px left-4 top-4 bottom-4 bg-border dark:bg-dark-border lg:left-1/2 lg:-translate-x-1/2"></div>
                  
                  <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-x-8">
                    {steps.map((step, index) => (
                      <motion.div 
                        key={step.number} 
                        className="relative lg:text-center"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.2, duration: 0.5 }}
                      >
                        <div className="flex items-center lg:justify-center">
                          {/* Use primary/accent for step number */}
                          <div className="z-10 flex items-center justify-center flex-shrink-0 w-8 h-8 font-bold rounded-full bg-primary dark:bg-dark-accent text-primary-foreground dark:text-dark-accent-foreground">
                            {step.number}
                          </div>
                          <div className="ml-4 lg:ml-0 lg:mt-4">
                            <h3 className="text-lg font-semibold text-foreground dark:text-dark-foreground">{step.title}</h3>
                          </div>
                        </div>
                        <div className="mt-2 ml-12 lg:ml-0 lg:mt-2">
                          <p className="text-base text-muted-foreground dark:text-dark-muted-foreground">{step.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-12 text-center">
                  <h3 className="mb-6 text-2xl font-bold text-primary dark:text-dark-accent">Ready to Get Started?</h3>
                  <div className="flex flex-col justify-center gap-4 sm:flex-row">
                    {/* Buttons use semantic variants */}
                    <Button
                      href="/solutions"
                      variant="primary"
                      size="lg"
                    >
                      Browse AI Solutions
                    </Button>
                    <Button
                      href="/contact"
                      variant="accent" 
                      size="lg"
                    >
                      Contact Our Team
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      {/* Use secondary background */}
      <section className="py-12 bg-secondary dark:bg-dark-secondary">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-primary dark:text-dark-accent">Frequently Asked Questions</h2>
            <p className="max-w-3xl mx-auto mt-4 text-lg text-secondary-foreground dark:text-dark-secondary-foreground">
              Common questions about implementing AI in South African SMMEs
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto mt-12">
            <div className="space-y-6">
              {/* FAQ Item - Use card styles */}
              <Disclosure as="div" className="rounded-lg bg-card dark:bg-dark-card shadow-soft">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-left rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                      <span className="text-lg font-medium text-primary dark:text-dark-accent">Do I need to understand coding to use these AI solutions?</span>
                      <ChevronDownIcon
                        className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground dark:text-dark-muted-foreground">
                      No, our AI solutions are designed to be user-friendly and require no coding knowledge. We handle all the technical setup and configuration, providing you with an intuitive interface to access the insights and benefits.
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
              {/* Repeat for other FAQ items using card/primary/accent/muted colors */} 
              <Disclosure as="div" className="rounded-lg bg-card dark:bg-dark-card shadow-soft">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-left rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                      <span className="text-lg font-medium text-primary dark:text-dark-accent">How much does it cost to implement AI in my business?</span>
                      <ChevronDownIcon
                        className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground dark:text-dark-muted-foreground">
                      Our AI solutions are specifically designed to be affordable for South African SMMEs. Prices range from R800 for basic tools to R5,000 for more comprehensive solutions, with reasonable monthly support fees. We offer Bronze, Silver, and Gold tiers to match different budget levels.
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
               <Disclosure as="div" className="rounded-lg bg-card dark:bg-dark-card shadow-soft">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-left rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                      <span className="text-lg font-medium text-primary dark:text-dark-accent">How long does it take to implement an AI solution?</span>
                      <ChevronDownIcon
                        className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground dark:text-dark-muted-foreground">
                      Most of our AI solutions can be implemented within 1-2 weeks. The process is designed to be minimally disruptive to your business operations, and we provide full support throughout the implementation phase.
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
               <Disclosure as="div" className="rounded-lg bg-card dark:bg-dark-card shadow-soft">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-left rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                      <span className="text-lg font-medium text-primary dark:text-dark-accent">Will AI tools work with my existing business systems?</span>
                      <ChevronDownIcon
                        className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground dark:text-dark-muted-foreground">
                      Yes, our AI solutions are designed to complement your existing business systems. We can configure the tools to work with your current data sources and business processes, ensuring a smooth integration into your operations.
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
              <Disclosure as="div" className="rounded-lg bg-card dark:bg-dark-card shadow-soft">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex items-center justify-between w-full px-6 py-4 text-left rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-ring dark:focus-visible:ring-dark-ring focus-visible:ring-opacity-75">
                      <span className="text-lg font-medium text-primary dark:text-dark-accent">What kind of support do you provide after implementation?</span>
                      <ChevronDownIcon
                         className={`${ open ? 'transform rotate-180' : '' } w-5 h-5 text-accent dark:text-dark-accent transition-transform duration-200`}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 py-4 text-base text-muted-foreground dark:text-dark-muted-foreground">
                      We provide ongoing support through a monthly subscription, which includes system maintenance, troubleshooting, and basic usage guidance. We're committed to ensuring you get maximum value from your AI investment.
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Use primary bg */}
      <section className="py-12 bg-primary dark:bg-dark-primary">
         <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
           <div className="text-center">
             <h2 className="text-3xl font-bold text-primary-foreground dark:text-dark-primary-foreground">Ready to Transform Your Business with AI?</h2>
             <p className="max-w-3xl mx-auto mt-4 text-xl text-primary-foreground/80 dark:text-dark-primary-foreground/80">
               Take the first step towards implementing AI solutions that address your specific business challenges.
             </p>
             <div className="flex flex-col justify-center gap-4 mt-8 sm:flex-row">
               {/* Buttons use semantic variants */}
               <Button
                 href="/solutions"
                 variant="accent" 
                 size="lg"
               >
                 Explore AI Solutions
               </Button>
               <Button
                 href="/contact"
                 variant="outline"
                 size="lg"
                 className="border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground/10 dark:border-dark-primary-foreground/50 dark:text-dark-primary-foreground dark:hover:bg-dark-primary-foreground/10"
               >
                 Contact Us
               </Button>
             </div>
           </div>
         </div>
       </section>
    </Layout>
  );
};

export default AIBasicsPage;
